import useSWR from "swr";
import { useServicesContext } from "../../providers/ServicesProvider";
import { useMemo } from "react";
import { DEFAULT_PAGE_SIZE, EVENT_TYPES, GROUP_TYPES, PROPERTY_KEYS, SWR_KEY_PREFIX } from "../../constants/constants";
import { User } from "../../domain/User";
import { UserGroup } from "../../domain/UserGroup";
import { useUpdateSync } from "../useUpdateSync/useUpdateSync";

export const useTeams = ({
    groupTypes = [GROUP_TYPES.CUSTOM],
    skip,
    organizationId,
    page = 1,
    query = "",
    productIds = [],
    instanceIds = []
}: {
    groupTypes?: string[];
    organizationId?: string;
    skip?: boolean;
    page?: number;
    query?: string;
    productIds?: string[];
    instanceIds?: string[];
}) => {
    const { teamsService } = useServicesContext();
    const { fireChangeEvent } = useUpdateSync();

    const productIdsKey = useMemo(
        () => (productIds.length > 0 ? JSON.stringify([...productIds].sort()) : ""),
        [productIds]
    );
    const instanceIdsKey = useMemo(
        () => (instanceIds.length > 0 ? JSON.stringify([...instanceIds].sort()) : ""),
        [instanceIds]
    );

    // Sort and deduplicate group types for consistent cache keys
    const normalizedGroupTypes = useMemo(() => Array.from(new Set(groupTypes)).sort(), [groupTypes]);

    const groupTypesKey = useMemo(
        () => (normalizedGroupTypes.length > 0 ? JSON.stringify(normalizedGroupTypes) : ""),
        [normalizedGroupTypes]
    );

    const key = useMemo(() => {
        if (!organizationId || skip) return null;
        const searchParams = new URLSearchParams(`properties[0].Name=${PROPERTY_KEYS.TEAM}&properties[0].Value=true`);
        searchParams.set("organizationId", organizationId);
        searchParams.set("page", page.toString());
        if (groupTypesKey) {
            const types = JSON.parse(groupTypesKey) as string[];
            types.forEach((groupType) => {
                searchParams.append("groupTypes", groupType);
            });
        }
        if (query) {
            searchParams.set("query", query);
        }
        if (productIdsKey) {
            searchParams.set("productIds", productIdsKey);
        }
        if (instanceIdsKey) {
            searchParams.set("instanceIds", instanceIdsKey);
        }
        return `${SWR_KEY_PREFIX.TEAMS}:/api/usergroups?${searchParams.toString()}`;
    }, [organizationId, page, skip, query, productIdsKey, instanceIdsKey, groupTypesKey]);

    const {
        data: teams,
        isValidating,
        error
    } = useSWR(key, () => {
        return teamsService!.getTeams({
            groupTypes: normalizedGroupTypes,
            organizationId: organizationId ?? "",
            page,
            query,
            productIds,
            instanceIds
        });
    });

    const addTeam = async (newTeam: UserGroup): Promise<UserGroup> => {
        const results = await teamsService!.addTeam(newTeam);

        fireChangeEvent({
            type: EVENT_TYPES.ADD_TEAM,
            data: {
                group: { id: results.id, name: results.name, description: results.description }
            }
        });

        return results;
    };

    const updateTeam = async (params: {
        name?: string;
        description?: string;
        team: UserGroup;
        usersToAdd?: User[];
        usersToRemove?: User[];
    }) => {
        const result = await teamsService!.updateTeam({
            team: params.team,
            description: params.description,
            name: params.name,
            usersToAdd: params.usersToAdd?.map((user) => user.email),
            usersToRemove: params.usersToRemove?.map((user) => user.email)
        });

        fireChangeEvent({
            type: EVENT_TYPES.UPDATE_TEAM,
            data: { group: result }
        });

        return result;
    };

    const deleteTeam = async ({ team }: { team: UserGroup }) => {
        const result = await teamsService!.deleteTeam({ teamId: team.id });
        fireChangeEvent({
            type: EVENT_TYPES.DELETE_TEAM,
            data: { groups: [team] }
        });
        return result;
    };

    return {
        deleteTeam,
        error,
        loading: isValidating,
        pageCount: teams ? Math.ceil(teams.totalCount / DEFAULT_PAGE_SIZE) : 0,
        teams: teams?.items || [],
        totalCount: teams?.totalCount,
        addTeam,
        updateTeam
    };
};
